import React, { useCallback, useEffect, useState } from "react";
import { Save, RotateCcw, Loader2, Edit3, AlertTriangle } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

/**
 * VideoIntroPromptEditor — power-user override for the 3-question set the
 * recorder walks talent through. Server enforces exactly 3 entries with
 * label + detail + window_seconds; window sum must be ≤ 60s.
 *
 * Renders as a collapsible section on the Talent Passport ("Customize
 * prompts") so senior candidates can lead with a signature-win prompt
 * instead of "who you are". Cleanly restores to defaults with one click.
 */
const _sumWindows = (rows) => rows.reduce((s, r) => s + (Number(r.window_seconds) || 0), 0);

export const VideoIntroPromptEditor = () => {
    const [prompts, setPrompts] = useState([]);
    const [isCustom, setIsCustom] = useState(false);
    const [maxSecs, setMaxSecs] = useState(60);
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);  // collapsed by default
    // Snapshot of the last-saved prompt set so we can detect unsaved
    // dirty edits before letting a Revert-to-template obliterate them.
    const [savedSnapshot, setSavedSnapshot] = useState([]);
    // Template revert confirmation — {tpl} while the user is being asked
    // "you'll lose your custom edits, continue?".
    const [revertConfirm, setRevertConfirm] = useState(null);
    // Curated templates loaded once when the editor opens — chips let
    // the talent one-click a proven starter instead of writing from scratch.
    const [templates, setTemplates] = useState([]);
    // Which template is currently being previewed (expanded row under the
    // chip). null = no preview open. Only one preview open at a time so
    // the UI doesn't get chaotic with 4 expanded panes.
    const [previewingId, setPreviewingId] = useState(null);
    // Template the talent originally applied (if any). Persisted on the
    // passport so it survives sessions. Powers the "Revert to X" button.
    const [startingTemplateId, setStartingTemplateId] = useState(null);
    // Track the template we're currently applying so PUT can send its id
    // for popularity analytics. Reset to null after successful save so a
    // hand-edit doesn't misattribute.
    const [pendingTemplateId, setPendingTemplateId] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/talent/video-intro/prompts");
            const loaded = data.prompts || [];
            setPrompts(loaded);
            setSavedSnapshot(loaded.map((p) => ({ ...p })));
            setIsCustom(!!data.is_custom);
            setMaxSecs(data.max_seconds_total || 60);
            setStartingTemplateId(data.starting_template_id || null);
        } catch (err) {
            console.warn("[VideoIntroPromptEditor] load failed:", err?.message || err);
        }
    }, []);
    const loadTemplates = useCallback(async () => {
        try {
            const { data } = await api.get("/talent/video-intro/prompt-templates");
            setTemplates(data?.templates || []);
        } catch { /* silent */ }
    }, []);
    useEffect(() => { load(); loadTemplates(); }, [load, loadTemplates]);

    // Compare current prompts to the last-saved snapshot to detect
    // unsaved edits. Cheap enough to run inline — 3 rows, 3 fields.
    const _dirty = (a, b) => {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return true;
        for (let i = 0; i < a.length; i++) {
            const x = a[i] || {}, y = b[i] || {};
            if ((x.label || "") !== (y.label || "")) return true;
            if ((x.detail || "") !== (y.detail || "")) return true;
            if ((Number(x.window_seconds) || 0) !== (Number(y.window_seconds) || 0)) return true;
        }
        return false;
    };

    const applyTemplateNow = (tpl) => {
        // Deep-clone so subsequent edits don't mutate the template.
        setPrompts(tpl.prompts.map((p) => ({ ...p })));
        setPreviewingId(null);
        setPendingTemplateId(tpl.id);  // sent on next Save for popularity + revert stamp
        toast(`Applied "${tpl.name}" template. Save to lock it in.`, { duration: 4000 });
    };

    const applyTemplate = (tpl) => {
        // If the current editor state has unsaved edits, block on a
        // confirmation dialog so a stray click on "Revert to X" can't
        // silently obliterate 5 minutes of custom prompt copy.
        if (_dirty(prompts, savedSnapshot)) {
            setRevertConfirm(tpl);
            return;
        }
        applyTemplateNow(tpl);
    };

    const update = (i, field, value) => {
        setPrompts((rows) => rows.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
    };

    const save = async () => {
        // Client-side validation mirrors the server for a clean UX.
        for (const [i, p] of prompts.entries()) {
            if (!(p.label || "").trim()) {
                toast.error(`Prompt #${i + 1}: label is required.`);
                return;
            }
            if ((Number(p.window_seconds) || 0) < 5) {
                toast.error(`Prompt #${i + 1}: window must be at least 5 seconds.`);
                return;
            }
        }
        const total = _sumWindows(prompts);
        if (total > maxSecs) {
            toast.error(`Prompt windows sum to ${total}s — must be ≤ ${maxSecs}s. Trim a few seconds and try again.`);
            return;
        }
        setBusy(true);
        try {
            const body = { prompts };
            if (pendingTemplateId) body.template_id = pendingTemplateId;
            const { data } = await api.put("/talent/video-intro/prompts", body);
            toast.success("Custom prompts saved — they'll show in the recorder next time.");
            setIsCustom(true);
            if (data?.template_id) setStartingTemplateId(data.template_id);
            setPendingTemplateId(null);
            // Snapshot the just-saved payload so the dirty-check on the
            // next revert click reflects the new baseline.
            setSavedSnapshot(prompts.map((p) => ({ ...p })));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const reset = async () => {
        if (!window.confirm("Reset to the default 3-question prompt set?")) return;
        setBusy(true);
        try {
            await api.delete("/talent/video-intro/prompts");
            toast.success("Prompts reset to defaults.");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const total = _sumWindows(prompts);
    const overBudget = total > maxSecs;

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="video-intro-prompt-editor-open"
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 inline-flex items-center gap-1.5"
            >
                <Edit3 size={11} /> Customize prompts{isCustom ? " · custom set active" : ""}
            </button>
        );
    }

    return (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="video-intro-prompt-editor">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-sm font-heading font-semibold text-foreground inline-flex items-center gap-1.5">
                        <Edit3 size={13} /> Customize your prompts
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        Rewrite the 3 questions the recorder walks you through. Windows must sum to ≤ {maxSecs}s.
                    </div>
                </div>
                <button
                    onClick={() => setOpen(false)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                    data-testid="video-intro-prompt-editor-close"
                >
                    Close
                </button>
            </div>

            {/* Template chips — one-click starters. Kept above the editor
                so it's the first thing a talent sees on open. Clicking a
                chip expands a preview underneath so talent can peek at the
                actual 3 prompts BEFORE committing. Explicit "Apply" button
                inside the preview is the only path that writes the prompts. */}
            {templates.length > 0 && (
                <div data-testid="video-intro-prompt-templates" className="border-b border-border pb-3 space-y-3">
                    {/* "Started from" badge + one-click revert. Only surfaces
                        when the user has customized (isCustom) AND we know
                        which curated set they started from. */}
                    {isCustom && startingTemplateId && (() => {
                        const tpl = templates.find((t) => t.id === startingTemplateId);
                        if (!tpl) return null;
                        return (
                            <div
                                data-testid="video-intro-started-from-badge"
                                className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.05] px-3 py-2 flex items-center justify-between gap-2 flex-wrap"
                            >
                                <div className="text-xs text-foreground">
                                    You started from{" "}
                                    <span className="font-semibold" data-testid="video-intro-started-from-name">
                                        {tpl.name}
                                    </span>.
                                </div>
                                <button
                                    type="button"
                                    onClick={() => applyTemplate(tpl)}
                                    disabled={busy}
                                    data-testid="video-intro-revert-to-template"
                                    className="text-xs px-3 py-1 rounded border border-emerald-500/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 inline-flex items-center gap-1 disabled:opacity-60"
                                >
                                    <RotateCcw size={11} /> Revert to {tpl.name}
                                </button>
                            </div>
                        );
                    })()}
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                        Start from a proven template
                        <span className="ml-2 text-slate-500 normal-case font-normal">
                            · sorted by popularity
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {templates.map((t) => {
                            const isOpen = previewingId === t.id;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setPreviewingId(isOpen ? null : t.id)}
                                    data-testid={`video-intro-prompt-template-${t.id}`}
                                    aria-expanded={isOpen}
                                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                        isOpen
                                            ? "border-emerald-500 bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 font-semibold"
                                            : "border-border bg-background hover:bg-emerald-500/10 hover:border-emerald-500/40 text-foreground"
                                    }`}
                                >
                                    {t.name}{isOpen ? " · previewing" : ""}
                                </button>
                            );
                        })}
                    </div>
                    {/* Expanded preview — shows the 3 prompt rows + windows
                        exactly as they'd land in the editor if Apply is clicked. */}
                    {previewingId && (() => {
                        const t = templates.find((x) => x.id === previewingId);
                        if (!t) return null;
                        const total = t.prompts.reduce((s, p) => s + (Number(p.window_seconds) || 0), 0);
                        return (
                            <div
                                data-testid={`video-intro-prompt-template-preview-${t.id}`}
                                className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.04] p-3 space-y-2"
                            >
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="text-sm font-heading font-semibold text-foreground">{t.name}</div>
                                        <div className="text-xs text-muted-foreground">{t.description}</div>
                                        {t.audience && (
                                            <div className="text-[11px] text-muted-foreground italic mt-0.5">
                                                Best for: {t.audience}
                                            </div>
                                        )}
                                    </div>
                                    <span className="text-[11px] font-mono text-muted-foreground shrink-0">
                                        {total}s total
                                    </span>
                                </div>
                                <ol className="space-y-1.5 mt-2">
                                    {t.prompts.map((p, i) => (
                                        <li
                                            key={p.key || i}
                                            data-testid={`video-intro-prompt-template-preview-row-${t.id}-${i}`}
                                            className="flex items-start gap-2 text-xs"
                                        >
                                            <span className="w-4 h-4 rounded-full bg-emerald-500/25 text-emerald-800 dark:text-emerald-300 font-semibold flex items-center justify-center shrink-0 text-[10px]">
                                                {i + 1}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <span className="font-semibold text-foreground">{p.label}</span>{" "}
                                                <span className="text-muted-foreground">— {p.detail}</span>
                                            </div>
                                            <span className="text-[10px] font-mono text-slate-500 shrink-0">
                                                {p.window_seconds}s
                                            </span>
                                        </li>
                                    ))}
                                </ol>
                                <div className="flex gap-2 pt-1">
                                    <button
                                        type="button"
                                        onClick={() => applyTemplate(t)}
                                        data-testid={`video-intro-prompt-template-apply-${t.id}`}
                                        className="btn-green text-xs px-3 py-1.5"
                                    >
                                        Apply this template
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPreviewingId(null)}
                                        data-testid={`video-intro-prompt-template-close-${t.id}`}
                                        className="btn-outline text-xs px-3 py-1.5"
                                    >
                                        Close preview
                                    </button>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}

            <div className="space-y-2">
                {prompts.map((p, i) => (
                    <div key={i} className="flex items-start gap-2 flex-wrap" data-testid={`video-intro-prompt-row-${i}`}>
                        <span className="w-6 h-6 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 text-xs font-semibold flex items-center justify-center shrink-0 mt-1">
                            {i + 1}
                        </span>
                        <div className="flex-1 min-w-[220px] space-y-1">
                            <input
                                type="text"
                                value={p.label || ""}
                                onChange={(e) => update(i, "label", e.target.value)}
                                placeholder="Prompt label (e.g. Signature win)"
                                maxLength={40}
                                data-testid={`video-intro-prompt-label-input-${i}`}
                                className="w-full text-sm px-3 py-1.5 rounded border border-border bg-background text-foreground"
                            />
                            <input
                                type="text"
                                value={p.detail || ""}
                                onChange={(e) => update(i, "detail", e.target.value)}
                                placeholder="One-line detail (shown as subtitle)"
                                maxLength={120}
                                data-testid={`video-intro-prompt-detail-input-${i}`}
                                className="w-full text-xs px-3 py-1.5 rounded border border-border bg-background text-muted-foreground"
                            />
                        </div>
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                min={5}
                                max={maxSecs}
                                value={p.window_seconds || 20}
                                onChange={(e) => update(i, "window_seconds", parseInt(e.target.value || "0", 10))}
                                data-testid={`video-intro-prompt-window-input-${i}`}
                                className="w-16 text-sm px-2 py-1.5 rounded border border-border bg-background text-foreground text-right tabular-nums"
                            />
                            <span className="text-xs text-muted-foreground">s</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-border">
                <div
                    className={`text-xs font-mono tabular-nums ${overBudget ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}
                    data-testid="video-intro-prompt-budget"
                >
                    Total: {total}s / {maxSecs}s
                    {overBudget && " — trim a few seconds"}
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={reset}
                        disabled={busy || !isCustom}
                        data-testid="video-intro-prompt-reset"
                        className="btn-outline text-xs px-3 py-1.5 disabled:opacity-60 inline-flex items-center gap-1"
                    >
                        <RotateCcw size={11} /> Defaults
                    </button>
                    <button
                        onClick={save}
                        disabled={busy || overBudget}
                        data-testid="video-intro-prompt-save"
                        className="btn-green text-xs px-3 py-1.5 disabled:opacity-60 inline-flex items-center gap-1"
                    >
                        {busy ? <><Loader2 size={11} className="animate-spin" /> Saving</> : <><Save size={11} /> Save</>}
                    </button>
                </div>
            </div>

            {/* Revert-lose-your-edits confirmation */}
            <Dialog
                open={revertConfirm !== null}
                onOpenChange={(o) => { if (!o) setRevertConfirm(null); }}
            >
                <DialogContent data-testid="video-intro-revert-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="inline-flex items-center gap-2">
                            <AlertTriangle size={16} className="text-amber-500" />
                            Discard your unsaved prompt edits?
                        </DialogTitle>
                        <DialogDescription>
                            You&apos;ve edited the prompts since you last saved. Applying
                            <strong> &ldquo;{revertConfirm?.name}&rdquo;</strong> will overwrite
                            all three rows with the template values — your current copy will be lost.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <button
                            type="button"
                            onClick={() => setRevertConfirm(null)}
                            data-testid="video-intro-revert-cancel"
                            className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
                        >
                            Keep my edits
                        </button>
                        <button
                            type="button"
                            onClick={() => { const t = revertConfirm; setRevertConfirm(null); if (t) applyTemplateNow(t); }}
                            data-testid="video-intro-revert-confirm"
                            className="inline-flex items-center gap-1 rounded-full bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                        >
                            <RotateCcw size={11} /> Overwrite with template
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default VideoIntroPromptEditor;
